import {
  type AccessCondition,
  type Condition,
  PermissionActionCategory,
  type Permission,
  type PermissionArea,
  PermissionType,
  type Role,
} from '@repo/api-gw-sdk';

export type PermissionDescriptor = Permission & {
  group?: string;
};

export const ManageBackupSettingsGroup = 'Manage backup settings';

const SearchWorkspaceDataGroup = 'Search and explore workspace data';
const RestoreWorkspaceDataGroup = 'Restore workspace data';

const permissions: Record<PermissionType, { group?: string }> = {
  [PermissionType.GlobalSettingsView]: {},
  [PermissionType.VaultAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.CostExplorerView]: {},
  [PermissionType.AuditLogsView]: {},
  [PermissionType.UserAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.SamlGroupAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsRotateSecret]: {
    group: 'Access management',
  },
  [PermissionType.CustomRolesManage]: {
    group: 'Access management',
  },
  [PermissionType.OauthAppsManage]: {
    group: 'Integrations',
  },
  [PermissionType.NotificationPoliciesManage]: {
    group: 'Integrations',
  },
  [PermissionType.PermissionIdUnspecified]: {},
  [PermissionType.DashboardView]: {},
  [PermissionType.SaasDashboardView]: {},
  [PermissionType.DashboardManage]: {},
  [PermissionType.AiAssistantUse]: {},
  [PermissionType.InventoryView]: {},
  [PermissionType.SaasInventoryView]: {},
  [PermissionType.JobsView]: {},
  [PermissionType.SaasJobsView]: {},
  [PermissionType.BackupSettingsView]: {
    group: 'Backup settings',
  },
  [PermissionType.ThreatDetectionCenterView]: {},
  [PermissionType.StorageGatewayView]: {},
  [PermissionType.DatabaseExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.FileExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.SaasMailSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupDataSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasTeamsSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.ThreatDetectionExclusionManage]: {},
  [PermissionType.FileExplorerDelete]: {},
  [PermissionType.StorageGatewayDataDownload]: {},
  [PermissionType.StorageGatewayMetadataView]: {},
  [PermissionType.DataCatalogView]: {},
  [PermissionType.DataCatalogQuery]: {},
  [PermissionType.DataCatalogPermissionsManage]: {
    group: 'Data catalog access control',
  },
  [PermissionType.DataCatalogTagsManage]: {
    group: 'Data catalog access control',
  },
  [PermissionType.SnapshotsTakeOrConvert]: {},
  [PermissionType.SaasSnapshotsTake]: {},
  [PermissionType.ResourcesRestore]: {},
  [PermissionType.ConfigurationValidate]: {},
  [PermissionType.SaasConfigurationValidate]: {},
  [PermissionType.DiscoveryTrigger]: {},
  [PermissionType.SaasDiscoveryTrigger]: {},
  [PermissionType.FilesRestore]: {
    group: 'Restore granular data',
  },
  [PermissionType.SaasMailRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasTeamsRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SourceAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.SaasSourceAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.RestoreAccountsView]: {
    group: 'Backup settings',
  },
  [PermissionType.DiscoveryConfigurationView]: {
    group: 'Backup settings',
  },
  [PermissionType.VaultsView]: {
    group: 'Backup settings',
  },
  [PermissionType.BackupPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.SaasBackupPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.ClassificationPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.ObjectFiltersView]: {
    group: 'Backup settings',
  },
  [PermissionType.BackupConfigurationView]: {
    group: 'Backup settings',
  },
  [PermissionType.ZeroEtlPoliciesView]: {
    group: 'Backup settings',
  },
  [PermissionType.RestoreTemplatesView]: {
    group: 'Backup settings',
  },
  [PermissionType.DatabasesQuery]: {
    group: 'Restore granular data',
  },
  [PermissionType.DataClassesOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.EnvironmentsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.DetectedAppsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.SensitivityLevelOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.NetworkClassificationOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.ControlViolationsMute]: {
    group: 'Resource exceptions',
  },
  [PermissionType.BackupExclusionManage]: {
    group: 'Resource exceptions',
  },
  [PermissionType.VaultsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasSourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RestoreAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DiscoveryConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ZeroEtlPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ControlsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RansomwareDetectionManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupScopeManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.StorageGatewayManage]: {
    group: 'Storage gateway',
  },
  [PermissionType.ActionApprovalRulesManage]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRulesView]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRequestsManage]: {},
  [PermissionType.MpaPoliciesManage]: {
    group: 'Access management',
  },
  [PermissionType.MpaPoliciesView]: {
    group: 'Access management',
  },
  [PermissionType.MpaRequestsManage]: {},
  [PermissionType.RestoreTemplatesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DatalakeView]: {
    group: 'Managed data lake',
  },
  [PermissionType.DatalakeOperate]: {
    group: 'Managed data lake',
  },
  [PermissionType.ComplianceView]: {
    group: 'Backup settings',
  },
  [PermissionType.ClassificationPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
};

export const Permissions = Object.fromEntries(
  Object.entries(permissions).filter(
    ([key]) => key !== PermissionType.PermissionIdUnspecified
  )
);

// The pre-families roles UI renders Global management as a fourth section beside the tiers. The
// API stopped expressing that — global management is an area — so the legacy bucket is a
// console-local vocabulary composed from areas + category.
export const GlobalManagementLegacyCategory = 'GLOBAL_MANAGEMENT';

type LegacyPermissionCategory =
  PermissionActionCategory | typeof GlobalManagementLegacyCategory;

const permissionCategories: Record<
  LegacyPermissionCategory,
  { title: string }
> = {
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_ADMINISTRATION]: {
    title: 'Administration',
  },
  [GlobalManagementLegacyCategory]: {
    title: 'Global management',
  },
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_OPERATION]: {
    title: 'Operation',
  },
  [PermissionActionCategory.PERMISSION_ACTION_CATEGORY_VIEWING]: {
    title: 'Viewing',
  },
};

export const PermissionCategories = Object.fromEntries(
  Object.entries(permissionCategories)
);

export enum PermissionFamily {
  GlobalManagement = 'GLOBAL_MANAGEMENT',
  Cloud = 'CLOUD',
  Saas = 'SAAS',
  StorageGateway = 'STORAGE_GATEWAY',
  Datalake = 'DATALAKE',
}

const familyOrder = [
  PermissionFamily.GlobalManagement,
  PermissionFamily.Cloud,
  PermissionFamily.Saas,
  PermissionFamily.StorageGateway,
  PermissionFamily.Datalake,
];

// The SDK enum and the local one share their string values, so mapping is a cast. Areas this build
// doesn't know are dropped: the gateway can ship a new area before the console that renders it.
// The sort keeps the chips in a stable order regardless of how the server ordered the list.
export const getPermissionFamilies = (
  permission: PermissionDescriptor
): PermissionFamily[] =>
  (permission.areas ?? [])
    .map((area: PermissionArea) => area as string as PermissionFamily)
    .filter((family) => familyOrder.includes(family))
    .sort((a, b) => familyOrder.indexOf(a) - familyOrder.indexOf(b));

// A permission the gateway shares with another provider still has to reach that provider's
// surface, so only the gateway-exclusive ones follow the gateway's availability.
// Reads the raw areas, not the families: an area this console doesn't know still means the
// permission is shared, and dropping it would hide the permission along with the gateway.
export const isStorageGatewayOnly = (permission: PermissionDescriptor) => {
  const areas = permission.areas ?? [];

  return (
    areas.length > 0 &&
    areas.every(
      (area: PermissionArea) =>
        (area as string) === PermissionFamily.StorageGateway
    )
  );
};

export const getPermissionSection = (
  permission: PermissionDescriptor
): PermissionActionCategory => permission.category;

// The category chip of the pre-families roles UI: global management is its own section there,
// while the families UI renders it as a family and files the permission under its real section.
export const getLegacyPermissionCategory = (
  permission: PermissionDescriptor
): LegacyPermissionCategory =>
  getPermissionFamilies(permission).includes(PermissionFamily.GlobalManagement)
    ? GlobalManagementLegacyCategory
    : permission.category;

export const isDatalakePermission = (permission: PermissionDescriptor) =>
  getPermissionFamilies(permission).includes(PermissionFamily.Datalake);

// A condition takes the category of the permissions it is attached to, as the backend infers it;
// an unattached one defaults to cloud resources there too.
export const isDatalakeCondition = (
  conditionId: string,
  role: Pick<Role, 'permissionGrants'>,
  permissions: Record<string, PermissionDescriptor>
) =>
  role.permissionGrants.some((grant) => {
    const permission = permissions[grant.permission];
    return (
      grant.accessConditionId === conditionId &&
      !!permission &&
      isDatalakePermission(permission)
    );
  });

const usesOnlyTagProperties = (condition: Condition | undefined): boolean =>
  !!condition &&
  (condition.conditions?.length
    ? condition.conditions.every(usesOnlyTagProperties)
    : condition.property === 'tagKeys' ||
      condition.property === 'tagKeyValues');

// An attached condition is offered only to permissions of its own category. A detached one has no
// category left, so a data-lake permission also offers it when it uses only tags, the one
// vocabulary both categories accept; otherwise it could never be attached to one again.
export const isConditionOfferedTo = (
  condition: AccessCondition,
  datalakePermission: boolean,
  role: Pick<Role, 'permissionGrants'>,
  permissions: Record<string, PermissionDescriptor>
) => {
  const attached = role.permissionGrants.some(
    (grant) => grant.accessConditionId === condition.id
  );
  if (attached || !datalakePermission) {
    return (
      isDatalakeCondition(condition.id, role, permissions) ===
      datalakePermission
    );
  }
  return usesOnlyTagProperties(condition.condition);
};
